import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Plus, QrCode, Send, History, ChevronRight, ArrowUpRight, ArrowDownLeft, Copy, Search, X, Wallet, IndianRupee, CheckCircle2, AlertCircle, Loader2, ScanLine, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Wallet Dashboard ───
function WalletDashboard({ balance, loading, onAddMoney, onSendMoney, onShowQR, onShowHistory, recentTxns }) {
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Balance Card */}
      <div className="mx-4 mt-4 rounded-2xl bg-gradient-to-br from-[#0f172a] to-[#1e293b] p-6 shadow-xl">
        <p className="text-[#94a3b8] text-xs font-medium tracking-wider uppercase">Available Balance</p>
        <div className="flex items-baseline gap-1 mt-2">
          <span className="text-white/60 text-xl font-medium">&#8377;</span>
          <span className="text-white text-4xl font-bold tracking-tight" data-testid="wallet-balance">
            {loading ? '...' : balance.toLocaleString('en-IN')}
          </span>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onAddMoney}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#10b981] hover:bg-[#059669] text-white font-semibold text-sm transition-colors active:scale-[0.97]"
            data-testid="add-money-btn"
          >
            <Plus className="w-4 h-4" /> Add Money
          </button>
          <button
            onClick={onSendMoney}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-colors active:scale-[0.97]"
            data-testid="send-money-btn"
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 mx-4 mt-4">
        <button
          onClick={onShowQR}
          className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl bg-white border border-[#e2e8f0] hover:border-[#10b981] hover:shadow-sm transition-all"
          data-testid="qr-code-btn"
        >
          <div className="w-10 h-10 rounded-full bg-[#ecfdf5] flex items-center justify-center">
            <QrCode className="w-5 h-5 text-[#10b981]" />
          </div>
          <span className="text-xs font-medium text-[#334155]">My QR</span>
        </button>
        <button
          onClick={onSendMoney}
          className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl bg-white border border-[#e2e8f0] hover:border-[#7c3aed] hover:shadow-sm transition-all"
          data-testid="scan-pay-btn"
        >
          <div className="w-10 h-10 rounded-full bg-[#f5f3ff] flex items-center justify-center">
            <ScanLine className="w-5 h-5 text-[#7c3aed]" />
          </div>
          <span className="text-xs font-medium text-[#334155]">Scan & Pay</span>
        </button>
        <button
          onClick={onShowHistory}
          className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl bg-white border border-[#e2e8f0] hover:border-[#0ea5e9] hover:shadow-sm transition-all"
          data-testid="history-btn"
        >
          <div className="w-10 h-10 rounded-full bg-[#f0f9ff] flex items-center justify-center">
            <History className="w-5 h-5 text-[#0ea5e9]" />
          </div>
          <span className="text-xs font-medium text-[#334155]">History</span>
        </button>
      </div>

      {/* Recent Transactions */}
      <div className="mx-4 mt-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0f172a]">Recent Transactions</h3>
          {recentTxns.length > 0 && (
            <button onClick={onShowHistory} className="text-xs font-medium text-[#7c3aed] flex items-center gap-0.5" data-testid="see-all-btn">
              See all <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
        {recentTxns.length === 0 ? (
          <div className="py-12 text-center">
            <Wallet className="w-12 h-12 mx-auto text-[#cbd5e1] mb-3" />
            <p className="text-sm text-[#94a3b8]">No transactions yet</p>
            <p className="text-xs text-[#cbd5e1] mt-1">Add money to get started</p>
          </div>
        ) : (
          <div className="space-y-1">
            {recentTxns.slice(0, 5).map((txn, i) => (
              <TransactionRow key={txn.txn_id || i} txn={txn} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Transaction Row ───
function TransactionRow({ txn }) {
  const isCredit = txn.type === 'credit';
  const isTopup = txn.category === 'topup';

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#f8fafc] transition-colors" data-testid={`txn-${txn.txn_id}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCredit ? 'bg-[#ecfdf5]' : 'bg-[#fef2f2]'}`}>
        {isTopup ? (
          <Plus className={`w-5 h-5 ${isCredit ? 'text-[#10b981]' : 'text-[#ef4444]'}`} />
        ) : isCredit ? (
          <ArrowDownLeft className="w-5 h-5 text-[#10b981]" />
        ) : (
          <ArrowUpRight className="w-5 h-5 text-[#ef4444]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0f172a] truncate">{txn.description}</p>
        <p className="text-xs text-[#94a3b8]">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-semibold ${isCredit ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
          {isCredit ? '+' : '-'}&#8377;{txn.amount.toLocaleString('en-IN')}
        </p>
      </div>
    </div>
  );
}

// ─── Add Money Screen ───
function AddMoneyScreen({ onBack, token, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const presets = [100, 200, 500, 1000, 2000, 5000];

  const handleAddMoney = async () => {
    const val = parseInt(amount);
    if (!val || val < 1) return toast.error('Enter a valid amount');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/wallet/add-money`, { amount: val }, { headers: { Authorization: `Bearer ${token}` } });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'CallChat Wallet',
        description: `Add ${val} INR to wallet`,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/api/wallet/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Added ${val} INR to wallet!`);
            onSuccess(verify.data.balance);
          } catch (e) {
            toast.error('Payment verification failed');
          }
        },
        modal: { ondismiss: () => setLoading(false) },
        theme: { color: '#10b981' },
      };
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Razorpay not loaded');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6">
          <span className="text-[#94a3b8] text-sm">Enter amount</span>
        </div>
        <div className="flex items-center gap-2 mb-6">
          <span className="text-3xl text-[#0f172a] font-medium">&#8377;</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="text-4xl font-bold text-[#0f172a] bg-transparent outline-none w-full placeholder:text-[#cbd5e1]"
            data-testid="add-money-input"
            autoFocus
          />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-8">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => setAmount(String(p))}
              className={`py-3 rounded-xl text-sm font-semibold transition-all border ${amount === String(p) ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'bg-white text-[#334155] border-[#e2e8f0] hover:border-[#94a3b8]'}`}
              data-testid={`preset-${p}`}
            >
              &#8377;{p.toLocaleString('en-IN')}
            </button>
          ))}
        </div>
        <Button
          onClick={handleAddMoney}
          disabled={loading || !amount || parseInt(amount) < 1}
          className="w-full py-6 rounded-xl bg-[#10b981] hover:bg-[#059669] text-white font-semibold text-base shadow-lg shadow-[#10b981]/20"
          data-testid="confirm-add-money"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Add ₹${parseInt(amount || 0).toLocaleString('en-IN')}`}
        </Button>
      </div>
    </div>
  );
}

// ─── QR Code Screen ───
function QRScreen({ onBack, token }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/wallet/qr`, { headers: { Authorization: `Bearer ${token}` } });
        setQrData(data);
      } catch (e) {
        toast.error('Failed to load QR');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const copyQRData = () => {
    if (qrData) {
      navigator.clipboard.writeText(qrData.qr_data).then(() => toast.success('QR data copied!'));
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6">
      {loading ? (
        <Loader2 className="w-8 h-8 animate-spin text-[#10b981]" />
      ) : qrData ? (
        <>
          <div className="bg-white p-4 rounded-2xl shadow-lg border border-[#e2e8f0]" data-testid="qr-image">
            <img src={qrData.qr_code} alt="Payment QR" className="w-56 h-56" />
          </div>
          <p className="text-lg font-semibold text-[#0f172a] mt-4">{qrData.name}</p>
          <p className="text-sm text-[#94a3b8]">{qrData.email}</p>
          <p className="text-xs text-[#cbd5e1] mt-2">Scan this code to pay me</p>
          <button onClick={copyQRData} className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f1f5f9] text-sm text-[#334155] hover:bg-[#e2e8f0] transition-colors" data-testid="copy-qr-btn">
            <Copy className="w-4 h-4" /> Copy payment link
          </button>
        </>
      ) : (
        <p className="text-[#94a3b8]">Failed to generate QR</p>
      )}
    </div>
  );
}

// ─── Send Money Screen ───
function SendMoneyScreen({ onBack, token, onSuccess, balance }) {
  const [step, setStep] = useState('find');
  const [searchQuery, setSearchQuery] = useState('');
  const [qrInput, setQrInput] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [txnResult, setTxnResult] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Clean up scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const searchUser = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const isEmail = searchQuery.includes('@');
      const params = isEmail ? { email: searchQuery.trim() } : { phone: searchQuery.trim() };
      const { data } = await axios.get(`${API}/api/wallet/lookup-user`, { params, headers: { Authorization: `Bearer ${token}` } });
      setRecipient(data);
      setStep('amount');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'User not found');
    } finally {
      setSearching(false);
    }
  };

  const handleQRPay = (inputData) => {
    const data = inputData || qrInput;
    try {
      const parsed = JSON.parse(data);
      if (parsed.type === 'callchat_pay' && parsed.user_id) {
        setRecipient({ id: parsed.user_id, name: parsed.name, email: parsed.email });
        setStep('amount');
        stopScanner();
      } else {
        toast.error('Invalid CallChat QR code');
      }
    } catch {
      toast.error('Invalid QR data — paste the full QR code text');
    }
  };

  const startScanner = async () => {
    setShowScanner(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      await new Promise(resolve => setTimeout(resolve, 300));
      const scannerId = 'qr-reader-' + Date.now();
      if (scannerRef.current) scannerRef.current.id = scannerId;
      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrCodeRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decodedText) => {
          handleQRPay(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error('QR Scanner error:', err);
      toast.error('Could not access camera. Please allow camera permissions or paste the QR data manually.');
      setShowScanner(false);
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    setShowScanner(false);
  };

  const handleSend = async () => {
    const val = parseInt(amount);
    if (!val || val < 1) return toast.error('Enter a valid amount');
    if (val > balance) return toast.error('Insufficient balance');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/wallet/pay`, {
        recipient_id: recipient.id,
        amount: val,
        note: note.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTxnResult(data);
      setStep('success');
      onSuccess(data.balance);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success' && txnResult) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 rounded-full bg-[#ecfdf5] flex items-center justify-center mb-4">
          <CheckCircle2 className="w-10 h-10 text-[#10b981]" />
        </div>
        <p className="text-xl font-bold text-[#0f172a]">&#8377;{parseInt(amount).toLocaleString('en-IN')}</p>
        <p className="text-sm text-[#94a3b8] mt-1">sent to {recipient.name || recipient.email}</p>
        {note && <p className="text-xs text-[#cbd5e1] mt-2 italic">"{note}"</p>}
        <Button onClick={onBack} className="mt-8 px-8 rounded-xl bg-[#0f172a] hover:bg-[#1e293b]" data-testid="done-btn">Done</Button>
      </div>
    );
  }

  if (step === 'amount') {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        {/* Recipient card */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#f1f5f9] mb-6">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-[#7c3aed] text-white text-sm">{(recipient.name || recipient.email || '?').charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0f172a] truncate">{recipient.name || 'User'}</p>
            <p className="text-xs text-[#94a3b8] truncate">{recipient.email}</p>
          </div>
          <button onClick={() => { setStep('find'); setRecipient(null); }} className="p-1.5 rounded-full hover:bg-white">
            <X className="w-4 h-4 text-[#94a3b8]" />
          </button>
        </div>

        {/* Amount input */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-3xl text-[#0f172a] font-medium">&#8377;</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="text-4xl font-bold text-[#0f172a] bg-transparent outline-none w-full placeholder:text-[#cbd5e1]"
            data-testid="send-amount-input"
            autoFocus
          />
        </div>
        <p className="text-xs text-[#94a3b8] mb-4">Balance: &#8377;{balance.toLocaleString('en-IN')}</p>

        {/* Note */}
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (optional)"
          className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] text-sm text-[#0f172a] placeholder:text-[#cbd5e1] focus:outline-none focus:border-[#10b981] mb-6"
          data-testid="send-note-input"
        />

        <Button
          onClick={handleSend}
          disabled={loading || !amount || parseInt(amount) < 1 || parseInt(amount) > balance}
          className="w-full py-6 rounded-xl bg-[#0f172a] hover:bg-[#1e293b] text-white font-semibold text-base"
          data-testid="confirm-send-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Send ₹${parseInt(amount || 0).toLocaleString('en-IN')}`}
        </Button>
      </div>
    );
  }

  // Step: find recipient
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <p className="text-sm text-[#94a3b8] mb-4">Send money to a CallChat user</p>

      {/* Search by email/phone */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#e2e8f0] focus-within:border-[#10b981]">
          <Search className="w-4 h-4 text-[#94a3b8] shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchUser()}
            placeholder="Enter email or phone"
            className="flex-1 text-sm text-[#0f172a] bg-transparent outline-none placeholder:text-[#cbd5e1]"
            data-testid="search-user-input"
          />
        </div>
        <Button onClick={searchUser} disabled={searching} className="rounded-xl bg-[#0f172a] px-4" data-testid="search-user-btn">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find'}
        </Button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-[#e2e8f0]" />
        <span className="text-xs text-[#94a3b8]">OR</span>
        <div className="flex-1 h-px bg-[#e2e8f0]" />
      </div>

      {/* QR code section */}
      <p className="text-sm font-medium text-[#334155] mb-2">Pay via QR Code</p>

      {/* Camera Scanner */}
      {showScanner ? (
        <div className="mb-3" data-testid="qr-scanner-container">
          <div className="relative rounded-xl overflow-hidden bg-black">
            <div ref={scannerRef} id="qr-reader" className="w-full" style={{ minHeight: 280 }} />
            <button
              onClick={stopScanner}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 z-10"
              data-testid="close-scanner-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
          <p className="text-xs text-[#94a3b8] mt-2 text-center">Point your camera at a CallChat QR code</p>
        </div>
      ) : (
        <button
          onClick={startScanner}
          className="w-full flex items-center justify-center gap-2 py-4 mb-3 rounded-xl border-2 border-dashed border-[#7c3aed]/30 bg-[#f5f3ff] hover:bg-[#ede9fe] text-[#7c3aed] font-semibold text-sm transition-colors"
          data-testid="open-scanner-btn"
        >
          <Camera className="w-5 h-5" /> Scan QR with Camera
        </button>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-[#e2e8f0]" />
        <span className="text-xs text-[#94a3b8]">or paste QR data</span>
        <div className="flex-1 h-px bg-[#e2e8f0]" />
      </div>

      {/* Manual QR paste */}
      <textarea
        value={qrInput}
        onChange={(e) => setQrInput(e.target.value)}
        placeholder='Paste QR code data here...'
        className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] text-sm text-[#0f172a] placeholder:text-[#cbd5e1] focus:outline-none focus:border-[#7c3aed] mb-3 h-20 resize-none"
        data-testid="qr-input"
      />
      <Button
        onClick={() => handleQRPay()}
        disabled={!qrInput.trim()}
        variant="outline"
        className="w-full rounded-xl py-5 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f5f3ff] font-semibold"
        data-testid="qr-pay-btn"
      >
        <QrCode className="w-4 h-4 mr-2" /> Pay with QR
      </Button>
    </div>
  );
}

// ─── Transaction History Screen ───
function TransactionHistory({ token, onBack }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');

  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter !== 'all') params.category = filter;
      const { data } = await axios.get(`${API}/api/wallet/transactions`, { params, headers: { Authorization: `Bearer ${token}` } });
      setTxns(data.transactions);
      setTotalPages(data.pages);
    } catch (e) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [token, page, filter]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const filters = [
    { label: 'All', value: 'all' },
    { label: 'Top-ups', value: 'topup' },
    { label: 'Transfers', value: 'transfer' },
  ];

  // Group by date
  const grouped = {};
  txns.forEach((t) => {
    const day = new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(t);
  });

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Filters */}
      <div className="flex gap-2 px-4 pt-3 pb-2 overflow-x-auto">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => { setFilter(f.value); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${filter === f.value ? 'bg-[#0f172a] text-white' : 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'}`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#94a3b8]" />
        </div>
      ) : txns.length === 0 ? (
        <div className="py-20 text-center">
          <History className="w-10 h-10 mx-auto text-[#cbd5e1] mb-3" />
          <p className="text-sm text-[#94a3b8]">No transactions found</p>
        </div>
      ) : (
        <div className="px-4 pb-4">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date} className="mb-4">
              <p className="text-xs font-medium text-[#94a3b8] mb-2 sticky top-0 bg-[#f8fafc] py-1">{date}</p>
              <div className="space-y-0.5">
                {items.map((txn, i) => (
                  <TransactionRow key={txn.txn_id || i} txn={txn} />
                ))}
              </div>
            </div>
          ))}
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <span className="text-xs text-[#94a3b8]">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Wallet View ───
const WalletView = ({ open, onClose, token }) => {
  const [screen, setScreen] = useState('dashboard');
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recentTxns, setRecentTxns] = useState([]);

  const fetchBalance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/wallet/balance`, { headers: { Authorization: `Bearer ${token}` } });
      setBalance(data.balance);
    } catch (e) {
      console.error('Wallet balance error:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRecentTxns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/wallet/transactions`, { params: { page: 1, limit: 5 }, headers: { Authorization: `Bearer ${token}` } });
      setRecentTxns(data.transactions);
    } catch (e) {}
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchBalance();
      fetchRecentTxns();
    }
  }, [open, fetchBalance, fetchRecentTxns]);

  const handleBalanceUpdate = (newBalance) => {
    if (typeof newBalance === 'number') setBalance(newBalance);
    else fetchBalance();
    fetchRecentTxns();
    setScreen('dashboard');
  };

  if (!open) return null;

  const titles = {
    dashboard: 'Wallet',
    addMoney: 'Add Money',
    sendMoney: 'Send Money',
    qr: 'My QR Code',
    history: 'Transactions',
  };

  const handleBack = () => {
    if (screen === 'dashboard') onClose();
    else setScreen('dashboard');
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#f8fafc] flex flex-col" data-testid="wallet-view">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-[#e2e8f0] shrink-0">
        <button onClick={handleBack} className="p-1.5 rounded-full hover:bg-[#f1f5f9] transition-colors" data-testid="wallet-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#0f172a]" />
        </button>
        <h2 className="text-base font-semibold text-[#0f172a]">{titles[screen]}</h2>
      </div>

      {/* Content */}
      {screen === 'dashboard' && (
        <WalletDashboard
          balance={balance}
          loading={loading}
          onAddMoney={() => setScreen('addMoney')}
          onSendMoney={() => setScreen('sendMoney')}
          onShowQR={() => setScreen('qr')}
          onShowHistory={() => setScreen('history')}
          recentTxns={recentTxns}
        />
      )}
      {screen === 'addMoney' && (
        <AddMoneyScreen onBack={() => setScreen('dashboard')} token={token} onSuccess={handleBalanceUpdate} />
      )}
      {screen === 'sendMoney' && (
        <SendMoneyScreen onBack={() => setScreen('dashboard')} token={token} onSuccess={handleBalanceUpdate} balance={balance} />
      )}
      {screen === 'qr' && (
        <QRScreen onBack={() => setScreen('dashboard')} token={token} />
      )}
      {screen === 'history' && (
        <TransactionHistory token={token} onBack={() => setScreen('dashboard')} />
      )}
    </div>
  );
};

export default WalletView;
